/**
 * Data Hub — Reset Pipeline Data.
 *
 * Destructive flow: choose dataset → choose stages → preview counts → confirm → delete.
 * Uses ExecBridge CommonModal + Button; Type A/B as radio option cards.
 * Reset is always scoped to one dataset (production or synthetic).
 */
import { useEffect, useState } from "react";

import { AlertTriangle, Database, FlaskConical, RotateCcw } from "lucide-react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchDataHubResetTargets,
  previewDataHubStageReset,
  runDataHubStageReset,
  type DataHubResetResult,
  type DataHubResetTarget,
} from "../../Store/Reducers/DataHubSlice";
import { describeError } from "../data/api";
import {
  DATASET_KINDS,
  DATASET_LABELS,
  getActiveDataset,
  type DatasetKind,
} from "../data/dataset";

/** Stages that remove committed production data. Must match the server's set. */
const DESTRUCTIVE = new Set([
  "timeseries",
  "summaries",
  "benchmarks",
  "period_summaries",
]);

/** Session labels that are not the tenant's real name in public.organizations. */
const PLACEHOLDER_ORG_NAMES = new Set([
  "",
  "organization",
  "active organization",
  "your organization",
]);

function isCanonicalOrgName(name: string | null | undefined): boolean {
  return Boolean(name && !PLACEHOLDER_ORG_NAMES.has(name.trim().toLowerCase()));
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgName?: string;
  onReset?: () => void;
};

export function ResetDialog({
  open,
  onOpenChange,
  orgName = "",
  onReset,
}: Props) {
  const dispatch = useAppDispatch();
  const [targets, setTargets] = useState<DataHubResetTarget[]>([]);
  const [presets, setPresets] = useState<Record<string, string[]>>({});
  const [canonicalName, setCanonicalName] = useState("");
  const [stages, setStages] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<DataHubResetResult | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<DataHubResetResult | null>(null);
  const [preset, setPreset] = useState<"type_a" | "type_b" | null>(null);
  const [dataset, setDataset] = useState<DatasetKind>(getActiveDataset);

  useEffect(() => {
    if (!open) return;
    setStages({});
    setPreview(null);
    setTyped("");
    setError(null);
    setDone(null);
    setPreset(null);
    setCanonicalName("");
    setDataset(getActiveDataset());
    dispatch(fetchDataHubResetTargets())
      .unwrap()
      .then(r => {
        setTargets(r.targets);
        setPresets(r.presets);
        const fromApi = (r.organization_name || "").trim();
        const fromProp = orgName.trim();
        setCanonicalName(
          isCanonicalOrgName(fromApi)
            ? fromApi
            : isCanonicalOrgName(fromProp)
              ? fromProp
              : ""
        );
      })
      .catch(err => setError(describeError(err)));
  }, [dispatch, open, orgName]);

  const selected = Object.keys(stages).filter(s => stages[s]);
  const needsConfirmation = selected.some(s => DESTRUCTIVE.has(s));
  const confirmed =
    !needsConfirmation ||
    (Boolean(canonicalName) &&
      typed.trim().toLowerCase() === canonicalName.trim().toLowerCase());
  const datasetLabel = DATASET_LABELS[dataset];

  const toggleStage = (stage: string) => {
    setPreview(null);
    setPreset(null);
    setStages(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  const applyPreset = (name: "type_a" | "type_b") => {
    setPreview(null);
    setPreset(name);
    const list = presets[name] ?? [];
    setStages(Object.fromEntries(list.map(s => [s, true])));
  };

  const selectDataset = (next: DatasetKind) => {
    if (next === dataset) return;
    setDataset(next);
    setPreview(null);
    setError(null);
  };

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview(
        await dispatch(previewDataHubStageReset({ stages, dataset })).unwrap()
      );
    } catch (err: any) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await dispatch(
        runDataHubStageReset({
          stages,
          confirmOrganizationName: needsConfirmation ? typed.trim() : undefined,
          dataset,
        })
      ).unwrap();
      setDone(result);
      onReset?.();
    } catch (err: any) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  return (
    <CommonModal
      isOpen={open}
      toggle={close}
      size="xl"
      usePortal
      title="Reset Pipeline Data"
      subtitle={
        <>
          Permanently deletes pipeline data for{" "}
          <strong>{canonicalName || orgName || "this organization"}</strong>.
          Nothing is selected by default, and nothing is deleted until you have
          seen the counts. Resets only the selected dataset (
          {datasetLabel.toLowerCase()}).
        </>
      }
      footer={
        <>
          <Button
            type="button"
            variant="outline-primary"
            onClick={close}
            disabled={busy}
          >
            {done ? "Close" : "Cancel"}
          </Button>
          {!done &&
            (preview ? (
              <Button
                type="button"
                variant="danger"
                disabled={busy || !confirmed || preview.total_rows === 0}
                loading={busy}
                onClick={() => void doReset()}
                icon={<RotateCcw className="h-4 w-4" />}
              >
                Delete {preview.total_rows.toLocaleString()} row
                {preview.total_rows === 1 ? "" : "s"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                disabled={busy || selected.length === 0}
                loading={busy}
                onClick={() => void doPreview()}
              >
                Preview ({datasetLabel})
              </Button>
            ))}
        </>
      }
    >
      {done ? (
        <div className="space-y-2">
          <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
            Deleted {done.total_deleted.toLocaleString()} row
            {done.total_deleted === 1 ? "" : "s"} from {datasetLabel}.
          </div>
          <div className="space-y-1 rounded-md border border-gray-200 p-3">
            {done.targets.map(t => (
              <div key={t.stage} className="flex justify-between text-xs">
                <span className="text-gray-700">{t.label}</span>
                <span className="tabular-nums text-gray-900">
                  {t.deleted.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Dataset to reset
            </legend>
            <TabsComponent
              tabs={DATASET_KINDS.map(kind => ({
                id: kind,
                label: DATASET_LABELS[kind],
                icon:
                  kind === "synthetic" ? (
                    <FlaskConical size={16} />
                  ) : (
                    <Database size={16} />
                  ),
                disabled: busy,
              }))}
              activeTab={dataset}
              onTabChange={id => selectDataset(id as DatasetKind)}
              variant="secondary-segmented"
              segmentedLayout="fit"
              className="rounded-full"
            />
            <p className="m-0 mt-1.5 text-xs leading-relaxed text-gray-500">
              {dataset === "synthetic"
                ? "Only synthetic rows are counted and deleted. Production is left untouched."
                : "Only production rows are counted and deleted. Synthetic is left untouched."}
            </p>
          </fieldset>

          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Reset scope (within {datasetLabel})
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                  preset === "type_a"
                    ? "border-purple-400 bg-purple-50 ring-1 ring-purple-300"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="data-hub-reset-preset"
                  value="type_a"
                  checked={preset === "type_a"}
                  className="mt-1 h-4 w-4 shrink-0 accent-purple-600"
                  onChange={() => applyPreset("type_a")}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">
                    Type A — Staging only
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Clears staging data for this dataset. Committed production
                    stages are left untouched.
                  </span>
                </span>
              </label>

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                  preset === "type_b"
                    ? "border-red-300 bg-red-50 ring-1 ring-red-200"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="data-hub-reset-preset"
                  value="type_b"
                  checked={preset === "type_b"}
                  className="mt-1 h-4 w-4 shrink-0 accent-red-600"
                  onChange={() => applyPreset("type_b")}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">
                    Type B — Staging + Production
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Removes staging and committed stages for this dataset.
                    Requires org name confirm.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <div className="max-h-[220px] space-y-1 overflow-auto rounded-md border border-gray-200 p-2">
            {targets.map(t => (
              <label
                key={t.stage}
                className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={!!stages[t.stage]}
                  onChange={() => toggleStage(t.stage)}
                  className="h-4 w-4 accent-purple-600"
                />
                <span className="flex-1 text-gray-800">{t.label}</span>
                {DESTRUCTIVE.has(t.stage) && (
                  <span className="rounded bg-red-100 px-1.5 text-[10px] font-medium text-red-800">
                    production
                  </span>
                )}
              </label>
            ))}
          </div>

          {preview && (
            <div className="rounded-md border border-gray-200 p-3">
              <p className="m-0 mb-1.5 text-sm font-semibold text-gray-900">
                This will delete {preview.total_rows.toLocaleString()}{" "}
                {datasetLabel.toLowerCase()} row
                {preview.total_rows === 1 ? "" : "s"}
              </p>
              <div className="space-y-1">
                {preview.targets.map(t => (
                  <div key={t.stage} className="flex justify-between text-xs">
                    <span className="text-gray-600">{t.label}</span>
                    <span className="tabular-nums text-gray-900">
                      {t.rows.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {needsConfirmation && preview && (
            <div className="space-y-1.5 rounded-md border border-red-200 bg-red-50 p-3">
              <p className="m-0 flex items-center gap-1.5 text-sm font-medium text-red-900">
                <AlertTriangle className="h-4 w-4" />
                This removes committed {datasetLabel.toLowerCase()} data
              </p>
              {canonicalName ? (
                <>
                  <p className="m-0 text-xs text-red-800">
                    Type <strong>{canonicalName}</strong> to confirm.
                  </p>
                  <input
                    value={typed}
                    onChange={e => setTyped(e.target.value)}
                    placeholder={canonicalName}
                    className="mt-1 h-8 w-full rounded border border-red-300 px-2 text-sm"
                  />
                </>
              ) : (
                <p className="m-0 text-xs text-red-800">
                  Could not load this organization&apos;s name. Refresh and try
                  again — a placeholder name will not confirm the delete.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>
      )}
    </CommonModal>
  );
}

export default ResetDialog;
